import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Alert,
} from '@/components/ui'
import { api } from '@/api/client'
import { Loader2, Key, Trash2, Check, Github } from 'lucide-react'

interface OAuthProviderConfig {
  enabled: boolean
  client_id: string | null
  configured: boolean
}

interface OrgConfigResponse {
  oauth: {
    google: OAuthProviderConfig
    github: OAuthProviderConfig
    discord: OAuthProviderConfig
    twitter: OAuthProviderConfig
    telegram: { enabled: boolean; configured: boolean }
  }
}

const providers = [
  {
    id: 'google',
    name: 'Google',
    icon: () => (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
          fill="currentColor"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="currentColor"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="currentColor"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="currentColor"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
    ),
    docsUrl: 'https://console.cloud.google.com/apis/credentials',
  },
  {
    id: 'github',
    name: 'GitHub',
    icon: Github,
    docsUrl: 'https://github.com/settings/developers',
  },
  {
    id: 'discord',
    name: 'Discord',
    icon: () => (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
      </svg>
    ),
    docsUrl: 'https://discord.com/developers/applications',
  },
  {
    id: 'twitter',
    name: 'Twitter/X',
    icon: () => (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    docsUrl: 'https://developer.twitter.com/en/portal/dashboard',
  },
]

export function AuthSettingsPage() {
  const { orgSlug } = useParams()
  const [loading, setLoading] = useState(true)
  const [config, setConfig] = useState<OrgConfigResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editingProvider, setEditingProvider] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Form state for editing
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')

  useEffect(() => {
    loadConfig()
  }, [orgSlug])

  const loadConfig = async () => {
    if (!orgSlug) return
    setLoading(true)
    setError(null)
    try {
      const response = await api.orgs.getConfig(orgSlug)
      setConfig(response)
    } catch (err) {
      console.error('Failed to load config:', err)
      setError('Failed to load authentication settings')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (providerId: string) => {
    const providerConfig = config?.oauth[providerId as keyof typeof config.oauth]
    setClientId((providerConfig as OAuthProviderConfig)?.client_id || '')
    setClientSecret('')
    setEditingProvider(providerId)
  }

  const handleCancel = () => {
    setEditingProvider(null)
    setClientId('')
    setClientSecret('')
  }

  const handleSave = async () => {
    if (!orgSlug || !editingProvider) return

    setSaving(true)
    setError(null)

    try {
      await api.orgs.setOAuthKeys(orgSlug, editingProvider, {
        client_id: clientId,
        client_secret: clientSecret,
      })
      await loadConfig()
      handleCancel()
    } catch (err) {
      console.error('Failed to save OAuth keys:', err)
      setError('Failed to save OAuth configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (providerId: string) => {
    if (!orgSlug) return
    if (!confirm(`Are you sure you want to remove ${providerId} OAuth configuration?`)) {
      return
    }

    try {
      await api.orgs.removeOAuthKeys(orgSlug, providerId)
      await loadConfig()
    } catch (err) {
      console.error('Failed to remove OAuth keys:', err)
      setError('Failed to remove OAuth configuration')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--text-secondary)]" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">
          OAuth Providers
        </h2>
        <p className="text-sm text-[var(--text-secondary)]">
          Configure social login providers for your users. Users will be able to sign in using these providers.
        </p>
      </div>

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="grid gap-4">
        {providers.map((provider) => {
          const providerConfig = config?.oauth[provider.id as keyof typeof config.oauth] as OAuthProviderConfig | undefined
          const isConfigured = providerConfig?.configured || false
          const isEditing = editingProvider === provider.id
          const Icon = provider.icon

          return (
            <Card key={provider.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[var(--bg-elevated)] flex items-center justify-center">
                      <Icon />
                    </div>
                    <div>
                      <CardTitle className="text-base">{provider.name}</CardTitle>
                      <CardDescription>
                        {isConfigured ? (
                          <span className="flex items-center gap-1 text-green-500">
                            <Check className="w-3 h-3" />
                            Configured
                          </span>
                        ) : (
                          'Not configured'
                        )}
                      </CardDescription>
                    </div>
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-2">
                      {isConfigured && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(provider.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEdit(provider.id)}
                      >
                        <Key className="w-4 h-4 mr-1" />
                        {isConfigured ? 'Update' : 'Configure'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>

              {isEditing && (
                <CardContent className="pt-0">
                  <div className="space-y-4 pt-4 border-t border-[var(--border-subtle)]">
                    <div className="space-y-2">
                      <Label>Client ID</Label>
                      <Input
                        value={clientId}
                        onChange={(e) => setClientId(e.target.value)}
                        placeholder="Enter client ID"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Client Secret</Label>
                      <Input
                        type="password"
                        value={clientSecret}
                        onChange={(e) => setClientSecret(e.target.value)}
                        placeholder={isConfigured ? '••••••••••••••••' : 'Enter client secret'}
                      />
                      {isConfigured && (
                        <p className="text-xs text-[var(--text-muted)]">
                          Leave blank to keep the existing secret
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <a
                        href={provider.docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-[var(--accent)] hover:underline"
                      >
                        Get credentials from {provider.name}
                      </a>
                      <div className="flex items-center gap-2">
                        <Button variant="secondary" onClick={handleCancel} disabled={saving}>
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSave}
                          loading={saving}
                          disabled={!clientId || (!clientSecret && !isConfigured)}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          )
        })}
      </div>

      <Card className="bg-[var(--bg-elevated)]">
        <CardContent className="py-4">
          <p className="text-sm text-[var(--text-secondary)]">
            After configuring a provider, users will see it as a login option on your authentication pages.
            Make sure to set the correct callback URL in each provider's developer console:
          </p>
          <code className="block mt-2 p-2 bg-[var(--bg-base)] rounded text-xs text-[var(--text-primary)]">
            https://api.{orgSlug}.u.software/org-auth/oauth/[provider]/callback
          </code>
        </CardContent>
      </Card>
    </div>
  )
}
